import React from "react";
import { FiActivity, FiCheckCircle, FiClock, FiCompass, FiLayers, FiLayout, FiRepeat, FiShield, FiSmartphone, FiTarget } from "react-icons/fi";
import {
  LuSettings,
  LuUsers,
  LuSmartphone,
  LuShieldCheck,
  LuPercent,
  LuShield,
  LuBuilding2,
  LuShoppingBag,
  LuClock,
  LuWallet,
  LuCpu,
  LuClipboardList,
  LuPalette,
  LuCode,
  LuBug,
  LuRocket,
} from "react-icons/lu";

// 1. Hero Block Configuration
export const mobileAppDesignHeroData = {
  backgroundImage: "/images/serviceBanners/mobileAppDesign.webp",
  whiteImage: "/images/serviceBanners/MobileAppW.webp",
  subTitle: "Create Future-Ready Mobile App Design Solutions",
  title: (
    <>
      Modern <br /> <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600">Mobile App Design</span> Solutions
    </>
  ),
  description:
    "We create intuitive and responsive mobile app designs focused on user experience, engagement, and seamless interaction, helping businesses deliver consistent and high quality digital experiences across Android and iOS platforms.",
  primaryButton: {
    title: "Book Design Consultation",
    onClick: () => console.log("Consultation clicked"),
  },
  secondaryButton: {
    title: "View Prototype Demos",
    onClick: () => console.log("Prototypes clicked"),
  },
  featureData: [
    {
      icon: "/images/services/website/high_performance.webp",
      title: "AI-Powered Design Solutions",
      desc: "Smart mobile app design experiences built for modern user engagement.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/responsive.webp",
      title: "Cloud-Ready Interfaces",
      desc: "Responsive and scalable app designs optimized for seamless digital experiences.",
      iconColor: "#22d3ee",
    },
    {
      icon: "/images/services/website/secure.webp",
      title: "Secure UI Architecture",
      desc: "User-focused interfaces designed with accessibility, consistency, and usability in mind.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/clean_code.webp",
      title: "Modern Design Systems",
      desc: "Clean, interactive, and scalable mobile app design solutions for modern businesses.",
      iconColor: "#34d399",
    },
  ],
  floatingIcons: [
    {
      image: "/images/services/website/reactjs.svg",
      top: "16%",
      right: "8%",
      containerSize: 80,
      iconSize: 60,
      glowColor: "rgba(168,85,247,0.35)",
      iconGlow: "rgba(168,85,247,0.9)",
    },
  ],
};
// 2. Stats Block
export const mobileDesignStatsData = [
  {
    icon: LuSettings,
    value: "150+",
    label: "Apps Built",
    colorClass: "text-[#00d2ff]",
    bgClass: "bg-[#00d2ff]/10",
    borderClass: "border-[#00d2ff]/20",
    glowClass: "shadow-[0_0_20px_rgba(0,210,255,0.15)]",
  },
  {
    icon: LuUsers,
    value: "90+",
    label: "Happy Clients",
    colorClass: "text-[#a855f7]",
    bgClass: "bg-[#a855f7]/10",
    borderClass: "border-[#a855f7]/20",
    glowClass: "shadow-[0_0_20px_rgba(168,85,247,0.15)]",
  },
  {
    icon: LuSmartphone,
    value: "50M+",
    label: "Downloads",
    colorClass: "text-[#06b6d4]",
    bgClass: "bg-[#06b6d4]/10",
    borderClass: "border-[#06b6d4]/20",
    glowClass: "shadow-[0_0_20px_rgba(6,182,212,0.15)]",
  },
  {
    icon: LuShieldCheck,
    value: "99.9%",
    label: "Crash-Free Apps",
    colorClass: "text-[#3b82f6]",
    bgClass: "bg-[#3b82f6]/10",
    borderClass: "border-[#3b82f6]/20",
    glowClass: "shadow-[0_0_20px_rgba(59,130,246,0.15)]",
  },
  {
    icon: LuPercent,
    value: "40%",
    label: "Faster Development",
    colorClass: "text-[#d946ef]",
    bgClass: "bg-[#d946ef]/10",
    borderClass: "border-[#d946ef]/20",
    glowClass: "shadow-[0_0_20px_rgba(217,70,239,0.15)]",
  },
  {
    icon: LuShield,
    value: "100%",
    label: "Client Satisfaction",
    colorClass: "text-[#6366f1]",
    bgClass: "bg-[#6366f1]/10",
    borderClass: "border-[#6366f1]/20",
    glowClass: "shadow-[0_0_20px_rgba(99,102,241,0.15)]",
  },
];

// 3. Solutions & Process Block (WhyChooseUs Data)
export const mobileDesignSolutionsAndProcessData = {
  buildSubtitle: "OUR CAPABILITIES",
  buildTitle: "Custom Mobile App Design Solutions for Every Business Need",
  buildCards: [
    {
      title: "Cross-Platform App Design",
      desc: "Create consistent and user-friendly app designs for both Android and iOS platforms.",
      icon: FiLayout,
      colorClass: "text-[#00d2ff]",
      bgClass: "bg-[#00d2ff]/10",
      borderClass: "border-[#00d2ff]/20 hover:border-[#00d2ff]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(0,210,255,0.25)]",
    },
    {
      title: "Enterprise App Design",
      desc: "Scalable mobile app interfaces designed for secure and efficient business operations.",
      icon: FiLayers,
      colorClass: "text-[#3b82f6]",
      bgClass: "bg-[#3b82f6]/10",
      borderClass: "border-[#3b82f6]/20 hover:border-[#3b82f6]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(59,130,246,0.25)]",
    },
    {
      title: "eCommerce App Design",
      desc: "Modern shopping app designs focused on seamless browsing and user engagement.",
      icon: FiActivity,
      colorClass: "text-[#a855f7]",
      bgClass: "bg-[#a855f7]/10",
      borderClass: "border-[#a855f7]/20 hover:border-[#a855f7]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(168,85,247,0.25)]",
    },
    {
      title: "On-Demand App Design",
      desc: "Interactive app experiences for booking, delivery, and real-time service platforms.",
      icon: FiCompass,
      colorClass: "text-[#06b6d4]",
      bgClass: "bg-[#06b6d4]/10",
      borderClass: "border-[#06b6d4]/20 hover:border-[#06b6d4]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(6,182,212,0.25)]",
    },
    {
      title: "FinTech App Design",
      desc: "Secure and intuitive fintech app interfaces built for modern digital transactions.",
      icon: FiLayers,
      colorClass: "text-[#34d399]",
      bgClass: "bg-[#34d399]/10",
      borderClass: "border-[#34d399]/20 hover:border-[#34d399]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(52,211,153,0.25)]",
    },
    {
      title: "Custom Mobile App Design",
      desc: "Tailored mobile app designs aligned with your business goals and user expectations.",
      icon: FiLayout,
      colorClass: "text-[#f59e0b]",
      bgClass: "bg-[#f59e0b]/10",
      borderClass: "border-[#f59e0b]/20 hover:border-[#f59e0b]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(245,158,11,0.25)]",
    },
  ],
  processSubtitle: "OUR METHODOLOGY",
  processTitle: "Our Strategic Mobile App Design Approach",
  processSteps: [
    {
      step: "01",
      title: "Discovery & Research",
      description: "We understand your business goals, user expectations, and app design requirements.",
      icon: LuUsers,
    },
    {
      step: "02",
      title: "Strategy & Planning",
      description: "We define user flows, interface structure, and a clear design roadmap.",
      icon: LuPalette,
    },
    {
      step: "03",
      title: "UI/UX Design",
      description: "We create intuitive, modern, and user-focused mobile app interfaces.",
      icon: LuCode,
    },
    {
      step: "04",
      title: "Prototype & Interaction",
      description: "Interactive prototypes designed to improve usability and user engagement.",
      icon: LuRocket,
    },
    {
      step: "05",
      title: "Design Testing",
      description: "We review responsiveness, accessibility, and interface consistency across devices.",
      icon: LuPalette,
    },
    {
      step: "06",
      title: "Delivery & Design Support",
      description: "Final design assets delivered with scalable design systems and ongoing support.",
      icon: LuRocket,
    },
  ],
  benefitsSubtitle: "WHY CHOOSE OUR MOBILE APP DESIGN SERVICES?",
  benefitsTitle: "Why Businesses Choose Mobile App Design Solutions",
  benefits: [
    "Cross-Platform Design Consistency",
    "Faster Design Workflow",
    "Modern Mobile UI/UX",
    "Smooth User Experience",
    "Efficient Design Process",
    "Scalable App Interfaces",
    "User-Focused Design Solutions",
  ],
  logoImage: "/images/serviceBanners/mobileAppDesign.webp",
  whiteLogoImage: "/images/serviceBanners/MobileAppW.webp",
};

// 4. Portfolio Data
export const mobileDesignPortfolioData = {
  subtitle: "OUR PROJECTS",
  title: "Native Experiences Built for Growth",
  buttonText: "View Interactive Demos",
  buttonLink: "#",
  apps: [
    {
      title: "FitLife Tracker",
      badge: "Health & Fitness",
      badgeColor: "bg-[#00d2ff]/10 text-[#00d2ff] border-[#00d2ff]/20",
      desc: "A native iOS/Android experience focusing on high-retention data entry and visualization.",
      downloads: "Figma Hi-Fi",
      rating: "5.0",
      image: "https://images.unsplash.com/photo-1616469829581-73993eb86b02?q=80&w=2070&auto=format&fit=crop",
    },
  ],
};

// 5. Technologies Data
export const mobileDesignTechnologiesData = {
  techTitle: "OUR MOBILE DESIGN STACK",
  technologies: [
    { name: "Figma", icon: "Palette" },
    { name: "Lottie", icon: "Zap" },
    { name: "Zeplin", icon: "Layout" },
    { name: "Principle", icon: "Activity" },
    { name: "FigJam", icon: "Users" },
  ],
  trustTitle: "TRUSTED BY MOBILE-FIRST BRANDS",
  trustClients: [
    { image: "/images/logo/google_logo.png", alt: "google" },
    { image: "/images/logo/microsoft_logo.png", alt: "microsoft" },
    { image: "/images/logo/amazon_logo.png", alt: "amazon" },
  ],
};

// 6. About Overview
export const mobileDesignAboutData = [
  {
    title: "Future Focused Mobile App Design Experiences",
    content: (
      <>
        Modern businesses require digital experiences that are visually engaging, intuitive, and aligned with evolving user expectations. Professional{" "}
        <b className="text-text-primary font-semibold">mobile app design services</b> help brands improve customer interaction and strengthen digital accessibility across mobile
        platforms. <b className="text-text-primary font-semibold">Tycho Technologies</b> creates user focused mobile experiences that support long term engagement, improve
        interface clarity, and deliver seamless interaction for growing digital products.
      </>
    ),
  },
  {
    title: "Strategic Mobile Design for Better User Interaction",
    content: (
      <>
        Businesses developing mobile applications need structured interface strategies that simplify navigation and improve customer retention. A trusted{" "}
        <b className="text-text-primary font-semibold">mobile ui ux design company</b> helps organizations build intuitive digital journeys that enhance usability and strengthen
        user satisfaction. Companies also invest in{" "}
        <a href="https://tychotechnologies.com/web-SaaS-dashboard-design-india" className="text-cyan-400 underline hover:text-cyan-300">
          saas dashboard design services
        </a>{" "}
        to create organized mobile interfaces that improve workflow visibility, accessibility, and overall digital product efficiency for modern business operations.
      </>
    ),
  },
  {
    title: "Scalable App Interfaces for Modern Digital Platforms",
    content: (
      <>
        The demand for seamless and visually optimized mobile experiences continues to grow across ecommerce, fintech, healthcare, and enterprise industries. Professional{" "}
        <b className="text-text-primary font-semibold">app interface design services</b> help businesses create structured digital products that improve interaction flow and
        strengthen mobile usability. Organizations also collaborate with an experienced{" "}
        <a href="https://tychotechnologies.com/ui-design-systems-india" className="text-cyan-400 underline hover:text-cyan-300">
          ui design system company
        </a>{" "}
        to maintain interface consistency, streamline design collaboration, and support scalable product development across multiple mobile environments.
      </>
    ),
  },
  {
    title: "Reliable Mobile App Design Services in India",
    content: (
      <>
        Businesses searching for modern mobile experiences often invest in custom <b className="text-text-primary font-semibold">mobile app ui design solutions</b> that align with
        their branding, audience expectations, and long term digital goals. Tycho Technologies helps brands looking for a reliable{" "}
        <b className="text-text-primary font-semibold">mobile app design company in india</b> with scalable interface strategies and future ready design workflows. Companies also
        rely on professional <b className="text-text-primary font-semibold">app ui ux design services india</b> providers to improve mobile engagement, strengthen customer
        experiences, and support sustainable digital growth.
      </>
    ),
  },
];
// 7. FAQs (Preserved)
export const mobileAppDesignFaqs = [
  {
    q: "Why is mobile app design important for my app’s success?",
    a: "Mobile app design is crucial because it determines how users interact with your app. A well-designed app provides an intuitive, user-friendly experience, keeps users engaged, and ensures your app stands out in a competitive market.",
  },
  {
    q: "What services does a mobile app design company in India provide?",
    a: "A mobile app design company in India offers services like UI/UX design, wireframing, prototyping, and interactive design. They create visually appealing and user-friendly apps that enhance engagement and ensure a smooth experience across all devices.",
  },
  {
    q: "Will we get clickable prototypes before development?",
    a: "Yes. We provide interactive prototypes in Figma so teams can review and validate flows before engineering starts.",
  },
  {
    q: "Do you provide developer handoff files?",
    a: "Yes. You receive organized design files, component states, and annotations for smooth design-to-development execution.",
  },
  {
    q: "How long does a typical mobile app design project take?",
    a: "Most projects take 4 to 10 weeks depending on scope, number of screens, complexity, and iteration depth.",
  },
];
